import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BankStatement, ListStatementsQuery, UploadStatementInput } from '@accounting/shared';
import { api, openFileInNewTab } from '../lib/api';

export const statementsKey = (companyId: string | null, filters: Partial<ListStatementsQuery> = {}) =>
  ['statements', companyId ?? 'none', filters.bankAccountId ?? 'all', filters.reconciliationId ?? 'all'] as const;

export function useStatements(
  companyId: string | null,
  filters: Partial<ListStatementsQuery> = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: statementsKey(companyId, filters),
    enabled:
      companyId !== null &&
      (options.enabled ?? (!!filters.bankAccountId || !!filters.reconciliationId)),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.bankAccountId) params.set('bankAccountId', filters.bankAccountId);
      if (filters.reconciliationId) params.set('reconciliationId', filters.reconciliationId);
      const qs = params.toString();
      return api
        .get<{ statements: BankStatement[] }>(`/companies/${companyId}/statements${qs ? '?' + qs : ''}`)
        .then((r) => r.statements);
    },
  });
}

export function useUploadStatement(companyId: string | null, bankAccountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadStatementInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ statement: BankStatement }>(
          `/companies/${companyId}/bank-accounts/${bankAccountId}/statements`,
          input,
        )
        .then((r) => r.statement);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['statements', companyId ?? 'none'] });
    },
  });
}

/**
 * Open the statement in a new tab. The API streams the bytes via Managed Identity
 * over the authenticated connection, so the (fully private) storage account is never
 * reached directly by the browser.
 */
export async function openStatement(companyId: string, statementId: string): Promise<void> {
  await openFileInNewTab(`/companies/${companyId}/statements/${statementId}/raw`);
}
